import { getViewportSize, hasVisibleArea, isInteractive, isRandomIdentifier, isRectInViewport, makeOverlaysOpaque, normalizeText, truncateAttrValue, Viewport } from './dom.js';
import { isHidden } from './dom.js';
import { probeViewport } from './probe.js';
import { VxTreeView } from './tree.js';
import { isObjectEmpty, iterate } from './util.js';

export const VX_NODE_REF_SYMBOL = Symbol('vx:ref');
export const VX_NODE_COUNTER_SYMBOL = Symbol('vx:counter');
export const VX_IGNORE_SYMBOL = Symbol('vx:ignore');

export const VX_IGNORE_TAGS = ['svg', 'script', 'noscript', 'style', 'link', 'meta'];

export const VX_TAG_PREFERENCE = [
    'a', 'iframe', 'input', 'button', 'select', 'textarea', 'img', 'label',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'section', 'article', 'main', 'header', 'footer', 'nav', 'aside',
    'form', 'input', 'textarea', 'select', 'option', 'fieldset', 'legend',
    'p', 'pre', 'code', 'blockquote', 'figure', 'figcaption',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'strong', 'em', 'sub', 'sup',
];

const VX_KEEP_SELECTOR = 'a, button, input, textarea, select, label, iframe, option, optgroup';

export type VxDomNode = Element | Text;

export interface VxNode {
    ref?: string;
    tagName?: string;
    children?: VxNode[];
    id?: string;
    classList?: string[];
    textContent?: string;
    hasVisibleArea?: boolean;
    isInteractive?: boolean;
    isOutsideViewport?: boolean;
    isProbeHit?: boolean;
    isKept?: boolean;
    attributes?: Record<string, string>;
}

export interface VxTreeOptions {
    frameId?: string;
    iframeRef?: string;
    skipImages?: boolean;
    viewportOnly?: boolean;
    probeViewport?: boolean;
    opaqueOverlays?: boolean;
    unnestDivs?: boolean;
    /**
     * When true (default), open shadow roots are traversed after the host’s light DOM children
     * so the tree matches a flattened view (closed shadow roots are still inaccessible).
     */
    flattenShadowDom?: boolean;
}

export type VxTransform = (vxNode: VxNode) => VxNode;

export class VxTreeParser {

    private frameId = '';
    private viewport: Viewport = { width: 0, height: 0 };
    private vxNodes: VxNode[] = [];
    private probeElements: Element[] = [];
    private domRefMap = new Map<string, Node>();

    private counter = 0;
    private usedCounters = new Set<number>();

    constructor(public options: VxTreeOptions = {}) {
        this.frameId = options.frameId ?? '';
    }

    parse() {
        this.viewport = getViewportSize();
        this.probeElements = probeViewport();
        this.usedCounters.clear();
        this.collectDomCounters(document.documentElement);
        const vxRoot = this.parseNode(document.documentElement, null)!;
        this.vxNodes = this.pruneRecursive(vxRoot);
    }

    getTree(): VxTreeView {
        return new VxTreeView(this.vxNodes, this.frameId, this.options.iframeRef);
    }

    getNodes(): VxNode[] {
        return this.vxNodes;
    }

    getDomMap() {
        return this.domRefMap;
    }

    private parseNode(node: Node, parent: VxNode | null): VxNode | null {
        if (!node || (node as any)[VX_IGNORE_SYMBOL]) {
            return null;
        }
        if (node instanceof Text) {
            // Single instances of Text nodes can only occur in "text"
            // (e.g. when regular text is interspersed with links and other elements)
            const textContent = normalizeText(node.textContent ?? '');
            if (textContent) {
                return {
                    textContent,
                    hasVisibleArea: parent?.hasVisibleArea,
                    isOutsideViewport: parent?.isOutsideViewport,
                    isProbeHit: parent?.isProbeHit,
                };
            }
            return null;
        }
        if (node instanceof Element) {
            if (this.options.opaqueOverlays) {
                makeOverlaysOpaque(node as HTMLElement);
            }
            return this.parseElement(node);
        }
        return null;
    }

    private parseElement(el: Element): VxNode | null {
        const skip = VX_IGNORE_TAGS.includes(el.tagName.toLowerCase()) || isHidden(el);
        if (skip) {
            this.clearRecursive(el);
            return null;
        }
        // OPTION and OPTGROUP are special cases, we evaluate their rects and visibility
        // checks using their parent SELECT element
        const parentEl = el.matches('option, optgroup') ? el.closest('select') ?? el : el;
        const rect = parentEl.getBoundingClientRect();
        const id = el.getAttribute('id') ?? '';
        const isProbeHit = this.isProbeHit(parentEl);
        const isInViewport = isRectInViewport(rect, this.viewport);
        const vxNode = this.makeElementNode(el, {
            tagName: el.tagName.toLowerCase(),
            id: isRandomIdentifier(id) ? undefined : id,
            classList: Array.from(el.classList)
                .filter(cls => !isRandomIdentifier(cls))
                .slice(0, 4),
            attributes: this.collectAttributes(el),
            hasVisibleArea: hasVisibleArea(parentEl),
            isInteractive: isInteractive(parentEl),
            isOutsideViewport: !isInViewport,
            isProbeHit,
            isKept: el.matches(VX_KEEP_SELECTOR),
        });
        const children: VxNode[] = [];
        for (const child of iterate(el.childNodes)) {
            const childNode = this.parseNode(child, vxNode);
            if (childNode != null) {
                children.push(childNode);
            }
        }
        if (this.options.flattenShadowDom !== false && el.shadowRoot) {
            for (const child of iterate(el.shadowRoot.childNodes)) {
                const childNode = this.parseNode(child, vxNode);
                if (childNode != null) {
                    children.push(childNode);
                }
            }
        }
        // Special case: if the only child is a text node, return a single node with the text content
        if (children.length === 1 && !children[0]?.tagName) {
            vxNode.textContent = normalizeText(children[0].textContent ?? '');
        } else {
            vxNode.children = children;
        }
        return vxNode;
    }

    private makeElementNode(el: Element, spec: Omit<VxNode, 'ref'>): VxNode {
        const existingRef = (el as any)[VX_NODE_REF_SYMBOL];
        let ref;
        if (existingRef === undefined) {
            const counter = this.nextCounterValue();
            ref = `${this.frameId}${counter}`;
            this.usedCounters.add(counter);
            (el as any)[VX_NODE_COUNTER_SYMBOL] = counter;
            (el as any)[VX_NODE_REF_SYMBOL] = ref;
        } else {
            ref = existingRef;
        }
        const vxNode: VxNode = {
            ...spec,
            ref,
        };
        this.domRefMap.set(ref, el);
        return vxNode;
    }

    private pruneRecursive(vxNode: VxNode | null): VxNode[] {
        if (!vxNode) {
            return [];
        }
        const children = vxNode.children ?? [];
        // Recursively prune children
        const newChildren = children.flatMap(child => this.pruneRecursive(child));
        // If the node itself is omitted, replace it with children
        if (this.shouldOmit(vxNode)) {
            return newChildren;
        }
        vxNode.children = newChildren;
        // Collapse single-child elements
        if (newChildren.length === 1) {
            const child = newChildren[0];
            return this.collapseSingleChild(vxNode, child);
        }
        return [vxNode];
    }

    private collapseSingleChild(parent: VxNode, child: VxNode): VxNode[] {
        const preferParent = this.collapsePreferParent(parent, child);
        const merged = preferParent ?
            this.collapseMerge(parent, child) :
            this.collapseMerge(child, parent);
        return this.pruneRecursive({
            ...merged,
            children: child.children,
            textContent: child.textContent,
        });
    }

    private collapsePreferParent(parent: VxNode, child: VxNode) {
        const parentRank = VX_TAG_PREFERENCE.indexOf(parent.tagName ?? '');
        const childRank = VX_TAG_PREFERENCE.indexOf(child.tagName ?? '');
        if (parentRank === -1 && childRank === -1) {
            const parentAttrCount = Object.keys(parent.attributes ?? {}).length;
            const childAttrCount = Object.keys(child.attributes ?? {}).length;
            return parentAttrCount > childAttrCount;
        }
        return parentRank !== -1 && (childRank === -1 || parentRank < childRank);
    }

    private collapseMerge(a: VxNode, b: VxNode): VxNode {
        return {
            ...b,
            ...a,
            attributes: { ...b.attributes, ...a.attributes },
        };
    }

    private shouldOmit(vxNode: VxNode): boolean {
        const tagName = vxNode.tagName ?? '';
        if (!vxNode.hasVisibleArea) {
            return true;
        }
        if (this.options.viewportOnly && vxNode.isOutsideViewport) {
            return true;
        }
        if (this.options.probeViewport && !vxNode.isProbeHit) {
            return true;
        }
        if (this.options.skipImages && vxNode.tagName === 'img') {
            return true;
        }
        const hasText = !!vxNode.textContent;
        const hasChildren = (vxNode.children ?? []).length > 0;
        const hasAttrs = !isObjectEmpty(vxNode.attributes ?? {});
        if (this.options.unnestDivs) {
            const isDivOrSpan = ['div', 'span'].includes(tagName);
            if (isDivOrSpan && hasChildren && !hasAttrs) {
                return true;
            }
        }
        if (vxNode.isKept) {
            return false;
        }
        return !hasText && !hasAttrs && !hasChildren;
    }

    private isProbeHit(el: Element): boolean {
        // TODO this is a bit brute-forcish, consider improving
        for (const probeEl of this.probeElements) {
            if (el.contains(probeEl)) {
                return true;
            }
        }
        return false;
    }

    private collectAttributes(el: Element): Record<string, string> {
        const attrs: Record<string, string> = {};
        for (const attr of iterate(el.attributes)) {
            if (['id', 'class', 'style'].includes(attr.name.toLowerCase())) {
                continue;
            }
            attrs[attr.name] = truncateAttrValue(attr.value);
        }
        return attrs;
    }

    private clearRecursive(node: Node) {
        delete (node as any)[VX_NODE_REF_SYMBOL];
        delete (node as any)[VX_NODE_COUNTER_SYMBOL];
        if (node instanceof Element) {
            for (const child of iterate(node.children)) {
                this.clearRecursive(child);
            }
            if (node.shadowRoot) {
                for (const child of iterate(node.shadowRoot.children)) {
                    this.clearRecursive(child);
                }
            }
        }
    }

    private nextCounterValue() {
        while (this.usedCounters.has(this.counter)) {
            this.counter += 1;
        }
        return this.counter;
    }

    private collectDomCounters(el: Element) {
        if (!el) {
            return;
        }
        const cnt = (el as any)[VX_NODE_COUNTER_SYMBOL];
        if (cnt !== undefined) {
            this.usedCounters.add(cnt);
        }
        for (const child of iterate(el.children)) {
            this.collectDomCounters(child);
        }
        if (el.shadowRoot) {
            for (const child of iterate(el.shadowRoot.children)) {
                this.collectDomCounters(child);
            }
        }
    }

}
